import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Typography,
    Card,
    CardBody,
    IconButton,
    Button,
    Input,
    Chip,
    Checkbox,
    Slider,
} from "@material-tailwind/react";
import {
    ClockIcon,
    UserGroupIcon,
    FireIcon,
    HeartIcon,
    XMarkIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    PlusIcon,
    ArrowDownTrayIcon
} from "@heroicons/react/24/outline";
import { generateRecipePdf } from '../utils/generateRecipePdf';
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import RecipeDetail from './RecipeDetail';
import { useLanguage } from '../contexts/LanguageContext';
import { languages } from '../languages';
import { mcDePageClass } from '../utils/branding';
import { filterHalalRecipes } from '../utils/halalFilter';
import { resolveImageUrl } from '../utils/imageUrl';

const API_BASE_URL = '/api';

// Voorgedefinieerde allergenen
const ALLERGENS = [
    'gluten',
    'lactose',
    'nuts',
    'peanuts',
    'soy',
    'eggs',
    'fish',
    'shellfish',
    'milk',
    'celery',
    'mustard',
    'sesame',
    'sulphites'
];

const Recipes = () => {
    const { language } = useLanguage();
    // Map befr to french and benl to nederlands for translations
    let translationLanguage = language;
    if (language === 'befr' || language === 'be-fr') translationLanguage = 'french';
    if (language === 'benl' || language === 'be-nl') translationLanguage = 'nederlands';
    if (language === 'bh-ar' || language === 'bahrain-arabic') translationLanguage = 'arabic';
    if (language === 'bh-en' || language === 'bahrain-english') translationLanguage = 'english';
    if (language === 'uae-ar' || language === 'uae-arabic') translationLanguage = 'arabic';
    if (language === 'uae-en' || language === 'uae-english') translationLanguage = 'english';
    if (language === 'sa-ar' || language === 'sa-arabic') translationLanguage = 'arabic';
    if (language === 'sa-en' || language === 'sa-english') translationLanguage = 'english';
    if (language === 'kw-ar' || language === 'kw-arabic') translationLanguage = 'arabic';
    if (language === 'kw-en' || language === 'kw-english') translationLanguage = 'english';
    if (language === 'qa-ar' || language === 'qa-arabic') translationLanguage = 'arabic';
    if (language === 'qa-en' || language === 'qa-english') translationLanguage = 'english';
    if (language === 'om-ar' || language === 'om-arabic') translationLanguage = 'arabic';
    if (language === 'om-en' || language === 'om-english') translationLanguage = 'english';
    if (language === 'iq-ar' || language === 'iq-arabic') translationLanguage = 'arabic';
    if (language === 'iq-en' || language === 'iq-english') translationLanguage = 'english';
    if (language === 'jo-ar' || language === 'jo-arabic') translationLanguage = 'arabic';
    if (language === 'jo-en' || language === 'jo-english') translationLanguage = 'english';
    if (language === 'lb-ar' || language === 'lb-arabic') translationLanguage = 'arabic';
    if (language === 'lb-en' || language === 'lb-english') translationLanguage = 'english';
    if (language === 'sy-ar' || language === 'sy-arabic') translationLanguage = 'arabic';
    if (language === 'sy-en' || language === 'sy-english') translationLanguage = 'english';
    if (language === 'ps-ar' || language === 'ps-arabic') translationLanguage = 'arabic';
    if (language === 'ps-en' || language === 'ps-english') translationLanguage = 'english';
    if (language === 'ye-ar' || language === 'ye-arabic') translationLanguage = 'arabic';
    if (language === 'ye-en' || language === 'ye-english') translationLanguage = 'english';
    if (language === 'eg-ar' || language === 'eg-arabic') translationLanguage = 'arabic';
    if (language === 'eg-en' || language === 'eg-english') translationLanguage = 'english';
    if (language === 'pl-pl') translationLanguage = 'polish';
    if (language === 'cyprus' || language === 'cy' || language === 'el-cy') translationLanguage = 'greek';

    const t = languages[translationLanguage] || languages['english']; // Fallback to English if translation not found

    // State voor recepten
    const [recipes, setRecipes] = useState([]);
    const [filteredRecipes, setFilteredRecipes] = useState([]);
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [selectedImageData, setSelectedImageData] = useState(null);
    const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State voor nieuwe ingredient input
    const [newIngredient, setNewIngredient] = useState('');

    // State voor filters
    const [filters, setFilters] = useState({
        searchQuery: '',
        ingredients: [],
        allergens: [],
        cookingTimeRange: [0, 300]
    });

    useEffect(() => {
        fetchRecipes();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [recipes, filters]);

    const fetchRecipes = async () => {
        try {
            setLoading(true);
            setError(null);
            const userId = localStorage.getItem('userId');
            const response = await axios.get(`${API_BASE_URL}/saved-recipes/${userId}`);
            
            // Apply halal filter for Arabic-speaking users
            const filteredData = filterHalalRecipes(response.data, language);
            
            setRecipes(filteredData);
            setFilteredRecipes(filteredData);
        } catch (error) {
            console.error('Error fetching recipes:', error);
            setError(t.errorFetchingRecipes);
        } finally {
            setLoading(false);
        }
    };

    const allergyOptions = [
        { value: 'gluten', label: `🌾 ${t.gluten}` },
        { value: 'nuts', label: `🥜 ${t.nuts}` },
        { value: 'lactose', label: `🥛 ${t.lactose}` },
        { value: 'soy', label: `🫘 ${t.soy}` },
        { value: 'shellfish', label: `🦐 ${t.shellfish}` },
        { value: 'fish', label: `🐟 ${t.fish}` },
        { value: 'eggs', label: `🥚 ${t.eggs}` },
        { value: 'peanuts', label: `🥜 ${t.peanuts}` },
        { value: 'sesame', label: `🫘 ${t.sesame}` },
        { value: 'sulphites', label: `🧪 ${t.sulphites}` },
        { value: 'mustard', label: `🌭 ${t.mustard}` },
        { value: 'celery', label: `🥬 ${t.celery}` },
        { value: 'lupin', label: `🌸 ${t.lupin}` },
        { value: 'molluscs', label: `🐚 ${t.molluscs}` },
    ];

    const applyFilters = () => {
        let filtered = [...recipes];

        // Tekst zoeken
        if (filters.searchQuery) {
            const query = filters.searchQuery.toLowerCase();
            filtered = filtered.filter(recipe =>
                recipe.name.toLowerCase().includes(query) ||
                recipe.ingredients.some(ing => ing.name.toLowerCase().includes(query))
            );
        }

        // Ingrediënten filter
        if (filters.ingredients.length > 0) {
            filtered = filtered.filter(recipe =>
                filters.ingredients.every(ingredient =>
                    recipe.ingredients.some(ing =>
                        ing.name.toLowerCase().includes(ingredient.toLowerCase())
                    )
                )
            );
        }

        // Allergenen filter -- check both allergens field and ingredient names
        if (filters.allergens.length > 0) {
            const selectedAllergens = filters.allergens.map(a => a.toLowerCase());
            filtered = filtered.filter(recipe => {
                if (recipe.allergens) {
                    const recipeAllergens = Array.isArray(recipe.allergens) ? recipe.allergens : JSON.parse(recipe.allergens);
                    if (recipeAllergens.some(ra => selectedAllergens.some(a => ra.toLowerCase().includes(a)))) {
                        return false;
                    }
                }
                if (recipe.ingredients) {
                    const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : JSON.parse(recipe.ingredients);
                    const ingredientText = ingredients.map(i => (i.name || i).toLowerCase()).join(' ');
                    if (selectedAllergens.some(allergen => ingredientText.includes(allergen))) {
                        return false;
                    }
                }
                return true;
            });
        }

        // Bereidingstijd filter
        if (filters.maxCookingTime < 300) {  // Alleen filteren als niet op maximum
            filtered = filtered.filter(recipe =>
                recipe.cooking_time <= filters.maxCookingTime
            );
        }

        setFilteredRecipes(filtered);
    };

    const handleDownloadPdf = async (event, recipe) => {
        event.stopPropagation();
        try {
            await generateRecipePdf(recipe, t, recipe.image_data);
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    const handleRecipeClick = (recipe) => {
        setSelectedRecipe(recipe);
        setSelectedImageData(recipe.image_data);
        setIsDetailDialogOpen(true);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAddIngredient = () => {
        if (newIngredient && !filters.ingredients.includes(newIngredient)) {
            handleFilterChange('ingredients', [...filters.ingredients, newIngredient]);
            setNewIngredient('');
        }
    };

    const handleRemoveIngredient = (ingredient) => {
        const updatedIngredients = filters.ingredients.filter(ing => ing !== ingredient);
        handleFilterChange('ingredients', updatedIngredients);
    };

    const handleAllergenToggle = (allergenValue) => {
        const updatedAllergens = filters.allergens.includes(allergenValue)
            ? filters.allergens.filter(a => a !== allergenValue)
            : [...filters.allergens, allergenValue];
        handleFilterChange('allergens', updatedAllergens);
    };

    const handleTimeRangeChange = (value) => {
        handleFilterChange('cookingTimeRange', value);
    };

    const resetFilters = () => {
        setFilters({
            searchQuery: '',
            ingredients: [],
            allergens: [],
            cookingTimeRange: [0, 300]
        });
        setNewIngredient('');
    };

    if (loading) {
        return (
            <div className={`flex justify-center items-center h-screen ${mcDePageClass(language)}`}>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`p-4 ${mcDePageClass(language)}`}>
                <Typography color="red" className="text-center">
                    {error}
                </Typography>
                <Button
                    color="blue"
                    onClick={fetchRecipes}
                    className="mt-4 mx-auto block"
                >
                    {t.tryAgain}
                </Button>
            </div>
        );
    }
    return (
        <div className={`p-4 ${mcDePageClass(language)}`}>
            <div className="flex justify-between items-center mb-6">
                <Typography variant="h4">{t.savedRecipes}</Typography>
                <Button
                    variant="text"
                    className="flex items-center gap-2"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <FunnelIcon className="h-5 w-5" />
                    {t.filters}
                </Button>
            </div>

            {/* Zoekbalk */}
            <div className="mb-6">
                <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                        type="text"
                        label={t.searchRecipes}
                        value={filters.searchQuery}
                        onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
                        className="pl-10"
                        shrink={true}
                    />
                </div>
            </div>

            {showFilters && (
                <Card className="mb-6">
                    <CardBody>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Ingrediënten Sectie */}
                            <div>
                                <Typography variant="h6" className="mb-2">{t.ingredients}</Typography>
                                <div className="flex gap-2 mb-2">
                                    <Input
                                        type="text"
                                        value={newIngredient}
                                        onChange={(e) => setNewIngredient(e.target.value)}
                                        label={t.addIngredient}
                                        className="flex-1"

                                    />
                                    <IconButton onClick={handleAddIngredient}>
                                        <PlusIcon className="h-4 w-4" />
                                    </IconButton>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {filters.ingredients.map((ingredient, index) => (
                                        <Chip
                                            key={index}
                                            value={ingredient}
                                            dismissible
                                            onClose={() => handleRemoveIngredient(ingredient)}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Allergenen Sectie */}
                            <div>
                                <Typography variant="h6" className="mb-2">{t.allergens}</Typography>
                                <div className="grid grid-cols-2 gap-2">
                                    {allergyOptions.map((allergen) => (
                                        <div key={allergen.value} className="flex items-center gap-2">
                                            <Checkbox
                                                checked={filters.allergens.includes(allergen.value)}
                                                onChange={() => handleAllergenToggle(allergen.value)}
                                                label={
                                                    <Typography className="flex items-center gap-1 text-sm">
                                                        {allergen.label}
                                                    </Typography>
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Bereidingstijd Slider - Volledige breedte */}
                            <div className="md:col-span-2">
                                <Typography variant="h6" className="mb-4">{t.cookingTime}</Typography>
                                <div className="md:col-span-2 px-4">
                                    <div className="flex justify-end items-center mb-2">
                                        <Typography variant="small" color="gray">
                                            {t.upTo} {filters.maxCookingTime} {t.minutes}
                                        </Typography>
                                    </div>
                                    <div className="w-full">
                                        <input
                                            type="range"
                                            min="15"
                                            max="300"
                                            step="15"
                                            value={filters.maxCookingTime}
                                            onChange={(e) => handleFilterChange('maxCookingTime', parseInt(e.target.value))}
                                            className="w-full h-2 bg-blue-100 rounded-lg appearance-none cursor-pointer accent-blue-500"
                                        />
                                    </div>
                                    <div className="flex justify-between mt-1">
                                        <Typography variant="small" color="gray">15 {t.minutes}</Typography>
                                        <Typography variant="small" color="gray">300 {t.minutes}</Typography>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Reset Filters Button */}
                        <Button
                            variant="text"
                            color="red"
                            className="mt-6"
                            onClick={resetFilters}
                        >
                            {t.resetFilters}
                        </Button>
                    </CardBody>
                </Card>
            )}


            {/* Recepten Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                {filteredRecipes.map((recipe, index) => (

                    <div
                        key={index}
                        className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer relative min-w-[280px] max-w-[320px] m-auto"
                        onClick={() => handleRecipeClick(recipe)}
                    >

                        <div className="relative">
                            {recipe.image_path ? (
                                <img
                                    src={resolveImageUrl(recipe.image_path)}
                                    alt={recipe.name}
                                    className="w-full p-2 block m-auto h-48 object-cover rounded-md"
                                />
                            ) : (
                                <div className="w-full h-48 bg-gray-200 animate-pulse"></div>
                            )}
                            <IconButton
                                color="black"
                                size="md"
                                variant="outlined"
                                className="!absolute top-4 right-4 bg-white border-none"
                                onClick={(e) => handleDownloadPdf(e, recipe)}
                            >
                                <ArrowDownTrayIcon className="h-5 w-5" />
                            </IconButton>
                        </div>
                        <div className="p-4">
                            <div className='flex gap-2 items-start'>
                                <h3 className="text-lg font-semibold mb-2">{recipe.name}</h3>
                                <div className="flex items-center">
                                    <HeartIconSolid className="h-5 w-5 mr-1 text-red-500" />
                                    <p>{recipe.likes || 0} {t.likes}</p>
                                </div>
                            </div>
                            <div className="flex items-center mb-2 gap-2">
                                <div className="flex items-center">
                                    <ClockIcon className="h-5 w-5 mr-1 text-blue-500" />
                                    <Typography variant='small' className='text-[12px]'>{recipe.cooking_time} {t.minutes}</Typography>
                                </div>
                                {recipe.servings && (
                                    <div className="flex items-center">
                                        <UserGroupIcon className="h-5 w-5 mr-1 text-green-500" />
                                        <Typography variant='small' className='text-[12px]'>{recipe.servings} {t.servings}</Typography>
                                    </div>
                                )}
                                <div className="flex items-center">
                                    <FireIcon className="h-5 w-5 mr-1 text-orange-500" />
                                    <Typography variant='small' className='text-[12px]'>{recipe.calories_per_serving} kcal</Typography>
                                </div>
                            </div>

                        </div>
                    </div>
                ))}
            </div>

            {/* Recipe Detail Modal */}
            {
                selectedRecipe && (
                    <RecipeDetail
                        recipe={selectedRecipe}
                        isOpen={isDetailDialogOpen}
                        onClose={() => setIsDetailDialogOpen(false)}
                        imageData={selectedImageData}
                    />
                )
            }
        </div >
    );
};

export default Recipes;
